import { Fade, Typography } from '@material-ui/core'
import { memo } from 'react'

import { PromotionStatsRow } from '../../graphql/promotions'
import {
  formatCount,
  formatMinorWithCurrency
} from '../../helpers/promotionAmount'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { STATUS_LABEL } from './OverviewTable'
import useStyles from './styles/OverviewTableMobile.styles'

interface OverviewTableMobileProps {
  rows: PromotionStatsRow[]
}

/**
 * The same rows as label-value cards, for a narrow viewport.
 *
 * WHY A SEPARATE COMPONENT rather than a media query on the grid: at 360px the five-column
 * grid gives each numeric column roughly 60px, so `1,200,000 IRR` breaks mid-token across
 * three lines and the header wraps too. `overflowWrap: 'anywhere'` stops it overflowing but
 * not from becoming a block of ragged fragments — the columns stay aligned and unreadable.
 *
 * Every sibling list screen in this portal ships this pair, including the promotion list one
 * directory away (PromotionsTableMobile + PromotionRowMobile). The convention is not
 * aspirational here; omitting it was the defect.
 *
 * Each figure carries its OWN label rather than relying on column position, which is what a
 * card layout costs and also what makes it read correctly to a screen reader without the
 * table roles the desktop grid needs.
 */
const OverviewTableMobile: React.FC<OverviewTableMobileProps> = ({ rows }) => {
  const classes = useStyles()

  if (rows.length === 0) {
    return (
      <Typography className={classes.empty} data-cy="OverviewEmpty">
        {getTranslate('No promotions match this filter.')}
      </Typography>
    )
  }

  return (
    <div data-cy="OverviewTableMobile">
      {rows.map((row) => (
        <Fade in timeout={600} key={row.promotionId}>
          <div className={classes.card} data-cy="OverviewRowMobile">
            <Typography className={classes.name}>
              <LocalizedLink to={`/promotions/${row.promotionId}`}>
                {row.name}
              </LocalizedLink>
            </Typography>
            <Typography variant="body2" className={classes.status}>
              {getTranslate(STATUS_LABEL[row.status] ?? row.status)}
            </Typography>

            <div className={classes.figures}>
              <div>
                <Typography variant="body2" className={classes.label}>
                  {getTranslate('Applied')}
                </Typography>
                <Typography>{formatCount(row.appliedCount)}</Typography>
                <Typography variant="body2">
                  {formatMinorWithCurrency(
                    row.discountMinor,
                    row.currency,
                    row.minorUnitDigits
                  )}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" className={classes.label}>
                  {getTranslate('In flight')}
                </Typography>
                <Typography>{formatCount(row.inFlightCount)}</Typography>
                <Typography variant="body2">
                  {formatMinorWithCurrency(
                    row.inFlightDiscountMinor,
                    row.currency,
                    row.minorUnitDigits
                  )}
                </Typography>
              </div>
            </div>
          </div>
        </Fade>
      ))}
    </div>
  )
}

export default memo(OverviewTableMobile)
